/**
 * lib/objectIntel.js — Iter 47 Object Intelligence client helpers.
 *
 * Wraps the unified `/api/objects/{type}/{id}/*` endpoints with optimistic
 * UI + offline-queue fallback (Iter 36 syncQueue). Every mutation:
 *   - POSTs immediately when online
 *   - Falls back to syncQueue.enqueue() on failure (offline / 5xx / slow)
 *   - Caller renders the optimistic shape returned for INSTANT feedback
 *
 * Object types: structure, pin, revision, measurement, utility_watch.
 */
import { api } from "@/lib/api";
import { enqueue } from "@/lib/syncQueue";

export const OBJECT_TYPES = [
  "structure", "pin", "revision", "measurement", "utility_watch",
  "alignment", "pipe", "conflict",
];

const scopeKey = (objectType, objectId, kind) =>
  `obj:${objectType}:${objectId}:${kind}`;

const isFatal = (err) => {
  const s = err?.response?.status || 0;
  // 4xx are permanent client errors EXCEPT 408 (timeout) and 429 (throttle).
  return s >= 400 && s < 500 && s !== 408 && s !== 425 && s !== 429;
};

// ─────────────────────────────────────────────────────────────
// Reads
// ─────────────────────────────────────────────────────────────

export async function fetchTimeline(objectType, objectId, { limit = 200 } = {}) {
  const { data } = await api.get(
    `/objects/${objectType}/${objectId}/timeline`,
    { params: { limit } },
  );
  return data?.events || [];
}

export async function fetchSummary(objectType, objectId) {
  const { data } = await api.get(`/objects/${objectType}/${objectId}/summary`);
  return data;
}

export async function fetchNotes(objectType, objectId) {
  const { data } = await api.get(`/objects/${objectType}/${objectId}/notes`);
  return data?.notes || [];
}

export async function fetchIssues(objectType, objectId) {
  const { data } = await api.get(`/objects/${objectType}/${objectId}/issues`);
  return data?.issues || [];
}

export async function fetchStatus(objectType, objectId) {
  const { data } = await api.get(`/objects/${objectType}/${objectId}/status`);
  return data;
}

// ─────────────────────────────────────────────────────────────
// Writes — each falls back to syncQueue on failure
// ─────────────────────────────────────────────────────────────

export async function addNote(objectType, objectId, note_text) {
  const endpoint = `/objects/${objectType}/${objectId}/notes`;
  const payload = { note_text };
  try {
    const { data } = await api.post(endpoint, payload);
    return { online: true, doc: data };
  } catch (err) {
    if (isFatal(err)) throw err;
    const optimistic = {
      id: `q_${Date.now().toString(36)}`,
      object_type: objectType, object_id: objectId,
      note_text, created_at: new Date().toISOString(),
      _pending: true,
    };
    await enqueue({
      op_type: "object.note_added",
      endpoint, method: "POST", payload,
      scope_key: scopeKey(objectType, objectId, "note"),
      optimistic,
    });
    return { online: false, doc: optimistic };
  }
}

export async function deleteNote(objectType, objectId, noteId) {
  const endpoint = `/objects/${objectType}/${objectId}/notes/${noteId}`;
  try {
    await api.delete(endpoint);
    return { online: true };
  } catch (err) {
    if (isFatal(err)) throw err;
    await enqueue({
      op_type: "object.note_deleted",
      endpoint, method: "DELETE",
      scope_key: scopeKey(objectType, objectId, "note"),
    });
    return { online: false };
  }
}

export async function openIssue(objectType, objectId, { title, description }) {
  const endpoint = `/objects/${objectType}/${objectId}/issues`;
  const payload = { title, description: description || null };
  try {
    const { data } = await api.post(endpoint, payload);
    return { online: true, doc: data };
  } catch (err) {
    if (isFatal(err)) throw err;
    const optimistic = {
      id: `q_${Date.now().toString(36)}`,
      object_type: objectType, object_id: objectId,
      title, description, status: "open",
      created_at: new Date().toISOString(),
      reopened_count: 0, _pending: true,
    };
    await enqueue({
      op_type: "object.issue_opened",
      endpoint, method: "POST", payload,
      scope_key: scopeKey(objectType, objectId, "issue"),
      optimistic,
    });
    return { online: false, doc: optimistic };
  }
}

export async function transitionIssue(objectType, objectId, issueId, toStatus, note) {
  const endpoint = `/objects/${objectType}/${objectId}/issues/${issueId}`;
  const payload = { status: toStatus, note: note || null };
  try {
    const { data } = await api.patch(endpoint, payload);
    return { online: true, doc: data };
  } catch (err) {
    if (isFatal(err)) throw err;
    await enqueue({
      op_type: `object.issue_${toStatus}`,
      endpoint, method: "PATCH", payload,
      scope_key: scopeKey(objectType, objectId, "issue"),
    });
    return { online: false };
  }
}

export async function updateStatus(objectType, objectId, status) {
  const endpoint = `/objects/${objectType}/${objectId}/status`;
  const payload = { status };
  try {
    const { data } = await api.patch(endpoint, payload);
    return { online: true, doc: data };
  } catch (err) {
    if (isFatal(err)) throw err;
    await enqueue({
      op_type: "object.status_changed",
      endpoint, method: "PATCH", payload,
      scope_key: scopeKey(objectType, objectId, "status"),
    });
    return { online: false };
  }
}

// ─────────────────────────────────────────────────────────────
// Issue lifecycle helper — what the next legal transitions are.
// Mirrors VALID_ISSUE_TRANSITIONS on the backend so the UI doesn't
// offer illegal flips. Keep in sync with services/object_intelligence.py.
// Iter 51 — expanded operational vocabulary (RULE 3).
// ─────────────────────────────────────────────────────────────
export const ISSUE_TRANSITIONS = {
  open: ["not_started", "in_progress", "action_required",
         "pending_verification", "pending_closure", "closed"],
  not_started: ["in_progress", "action_required", "closed"],
  in_progress: ["action_required", "pending_verification",
                "pending_closure", "closed"],
  action_required: ["in_progress", "pending_verification", "closed"],
  pending_verification: ["in_progress", "action_required",
                          "pending_closure", "closed"],
  pending_closure: ["in_progress", "closed", "reopened"],
  closed: ["reopened"],
  reopened: ["in_progress", "action_required",
             "pending_verification", "pending_closure", "closed"],
};

export const ISSUE_STATUS_LABEL = {
  open: "Not Started",
  not_started: "Not Started",
  in_progress: "In Progress",
  action_required: "Action Required",
  pending_verification: "Pending Verification",
  pending_closure: "Pending Closure",
  closed: "Closed",
  reopened: "Reopened",
};

export const OBJECT_STATUS_LABEL = {
  open: "Not Started",
  not_started: "Not Started",
  in_progress: "In Progress",
  action_required: "Action Required",
  pending_verification: "Pending Verification",
  pending_closure: "Pending Closure",
  closed: "Closed",
  reopened: "Reopened",
};

// Iter 51 — color token per status, used by status chip + picker.
export const OBJECT_STATUS_TONE = {
  not_started: "var(--fop-text-3)",
  open: "var(--fop-text-3)",
  in_progress: "var(--fop-primary)",
  action_required: "var(--st-escalated, #ef4444)",
  pending_verification: "#f59e0b",
  pending_closure: "#0ea5e9",
  closed: "#10b981",
  reopened: "#a855f7",
};

// Iter 51 — assignment update (status PATCH accepts assigned_to too).
export async function updateAssignment(objectType, objectId, assignedTo) {
  const endpoint = `/objects/${objectType}/${objectId}/status`;
  const payload = { assigned_to: assignedTo ?? "" };
  try {
    const { data } = await api.patch(endpoint, payload);
    return { online: true, doc: data };
  } catch (err) {
    if (isFatal(err)) throw err;
    await enqueue({
      op_type: "object.assigned",
      endpoint, method: "PATCH", payload,
      scope_key: scopeKey(objectType, objectId, "assignment"),
    });
    return { online: false };
  }
}

// Iter 51 — fetch revision impacts for any object.
export async function fetchRevisionImpacts(objectType, objectId) {
  const { data } = await api.get(
    `/objects/${objectType}/${objectId}/revision_impacts`);
  return data?.revision_impacts || [];
}
